import { apiFetch } from '@/shared/api/client';

/**
 * Enriched comprobante row returned by the Administración backend: the
 * base `04.ComprobantePagos` fields plus a server-resolved `clienteRO`
 * (looked up from the parent venta's header) and `monedaRO`.
 */
export type ComprobanteAdminFields = {
  Title?: string | null;
  IDOperacion_CP?: string | null;
  Cliente_CP?: string | null;
  MedioPago_CP?: string | null;
  Monto_CP?: string | null;
  Pesos_CP?: string | null;
  TipoCambio_CP?: string | null;
  Fecha_CP?: string | null;
  FechaCobro_CP?: string | null;
  FechaPago_CP?: string | null;
  Status_P?: string | null;
  EsPagoFinal_CP?: string | null;
  ContieneFactura_CP?: string | null;
  Facturado_CP?: string | null;
  NroFactura_CP?: string | null;
  IVA_CP?: string | null;
  PorcentajeIVA_CP?: string | null;
  /** Factura PDF admin issued to the client (FacturaVenta uploader). */
  FacturaDriveItemID_CP?: string | null;
  FacturaFileName_CP?: string | null;
  /**
   * MX-only Factura Anticipada — uploaded first when the cobro is an
   * Anticipo (`EsPagoFinal_CP!='SI'`) that requires factura
   * (`ContieneFactura_CP='SI'`). The Final factura is uploaded later
   * into the regular `FacturaDriveItemID_CP` pair. Always null on AR.
   */
  FacturaAnticipadaDriveItemID_CP?: string | null;
  FacturaAnticipadaFileName_CP?: string | null;
  NroFacturaAnticipada_CP?: string | null;
  /** Proof-of-payment the sales rep attached when registering the pago. */
  ComprobanteDriveItemID_CP?: string | null;
  ComprobanteFileName_CP?: string | null;
  /** MX Nova flag — auto-generated Factura Interna pending fiscal upload. */
  EsFacturaInterna_CP?: 'SI' | 'NO' | null;
  /** AR: cobro autorizado a facturarse ANTES de cobrarse (plata no ingresada). */
  FacturacionPrevia_CP?: 'SI' | 'NO' | null;
  IDNova_CP?: string | null;
};

export type ComprobanteAdminItem = {
  id: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  fields: ComprobanteAdminFields;
  clienteRO: string | null;
  monedaRO: 'ARS' | 'MXN' | 'USD' | null;
  /** MX Nova only — see `EnrichedComprobante` on the server. */
  facturaInternaUsado: boolean;
  /**
   * Datos fiscales del receptor para mostrar (read-only) en el diálogo de
   * Facturación de MX. Sólo lo llena `/facturacion`; `null` en el resto.
   */
  fiscal_data?: NovaFiscalData | null;
};

// --------------------------------------------------------------------------
// Reads
// --------------------------------------------------------------------------

/**
 * Channel split for the Cuentas por Cobrar (promesas) queue. On AR
 * everything is 'fornitalia' (no Nova medios exist). On MX:
 *   - 'fornitalia' (default) → non-Nova-channel promesas
 *   - 'nova'                  → Nova-channel promesas (Nova Banorte,
 *                               Nova Mercado Pago, Herconstru)
 * Routing happens server-side by `MedioPago_CP` match.
 */
export type CuentasPorCobrarChannel = 'fornitalia' | 'nova';

export async function listCuentasPorCobrar(
  channel: CuentasPorCobrarChannel = 'fornitalia',
): Promise<ComprobanteAdminItem[]> {
  const qs = channel === 'fornitalia' ? '' : `?channel=${channel}`;
  const res = await apiFetch<{ items: ComprobanteAdminItem[] }>(
    `/api/administracion/cuentas-por-cobrar${qs}`,
  );
  return res.items;
}

/** Promesa de cobro abierta para los avisos de Inicio (server: /api/promesas-cobro). */
export type PromesaCobro = {
  id: string;
  ventaSpId: string | null;
  nroCotizacion: string | null;
  cliente: string | null;
  vendedor: string | null;
  medioPago: string | null;
  /** En la moneda de la caja. */
  monto: number;
  moneda: 'ARS' | 'USD';
  /** 'YYYY-MM-DD'. */
  fechaCobro: string;
  esPagoFinal: boolean;
};

/** Admin/administración: todas; ventas/marketing: las de sus ventas (lo filtra el server). */
export async function listPromesasCobro(): Promise<PromesaCobro[]> {
  const res = await apiFetch<{ items: PromesaCobro[] }>('/api/promesas-cobro');
  return res.items;
}

export async function listCobrosAConfirmar(): Promise<ComprobanteAdminItem[]> {
  const res = await apiFetch<{ items: ComprobanteAdminItem[] }>(
    '/api/administracion/cobros',
  );
  return res.items;
}

export type FacturacionType = 'ventas' | 'internas' | 'nova' | 'todas';

export async function listFacturacionPendiente(
  type: FacturacionType = 'ventas',
): Promise<ComprobanteAdminItem[]> {
  const qs = type === 'ventas' ? '' : `?type=${type}`;
  const res = await apiFetch<{ items: ComprobanteAdminItem[] }>(
    `/api/administracion/facturacion${qs}`,
  );
  return res.items;
}

/**
 * Receptor fiscal data joined from `contacts` so Nova has everything
 * it needs to stamp the CFDI 4.0 factura on confirm. Null when the
 * venta has no linked contacto (legacy or text-only).
 */
export type NovaFiscalData = {
  rfc: string | null;
  razon_social: string | null;
  /** Régimen Fiscal del SAT (e.g. "601 - General de Ley Personas Morales"). */
  regimen_fiscal: string | null;
  /** Código postal del receptor (5 dígitos). */
  codigo_postal: string | null;
  /** Uso del CFDI code (e.g. "G03"). */
  uso_cfdi: string | null;
  email: string | null;
  direccion_completa: string | null;
};

/**
 * MX Nova flow — Cuentas por Cobrar - Nova queue. Returns the
 * Anticipos auto-created in PR 3.3.d that are awaiting Nova rol's
 * confirmation. Confirming via `confirmarCobroNova` flips the row to
 * Confirmado and writes the Tesorería Ingreso (+ optional 5% comisión
 * for Herconstru-style cajas).
 */
export type CobroNovaItem = {
  id: string;
  sales_header_sharepoint_id: number | null;
  nro_cotizacion: string | null;
  cliente: string | null;
  medio_pago_origen: string | null;
  monto: number | null;
  fecha_creacion: string | null;
  status: string | null;
  /** Comprobante de pago metadata, resolved by the backend from the
   *  originating operator receipt (the file never lives on the Anticipo
   *  itself). `comprobante_sp_id` carries the SharePoint id of that
   *  receipt — pass it as `FileLink entityId` so the download endpoint
   *  reads the row that actually owns the drive item. */
  comprobante_file_name: string | null;
  comprobante_drive_item_id: string | null;
  comprobante_sp_id: string | null;
  /** Receptor fiscal data — see `NovaFiscalData`. */
  fiscal_data: NovaFiscalData | null;
};

export async function listCobrosNova(): Promise<CobroNovaItem[]> {
  const res = await apiFetch<{ items: CobroNovaItem[] }>(
    '/api/administracion/cobros-nova',
  );
  return res.items;
}

export type ConfirmarCobroNovaResult = {
  ok: boolean;
  anticipoId: string;
  ingresoId: number;
  egresoId: number | null;
};

export async function confirmarCobroNova(
  anticipoId: string,
): Promise<ConfirmarCobroNovaResult> {
  return apiFetch<ConfirmarCobroNovaResult>(
    '/api/administracion/cobros-nova/confirmar',
    { method: 'POST', body: { id: anticipoId } },
  );
}

/**
 * MX Nova flow — inter-co Pagar/Cobrar queues. Both queues read from
 * `cash_movements` (the pair generated by `/facturar` for Factura
 * Interna rows in PR 3.3.e.2). Lógica de Freno: the Cobrar leg starts
 * locked; Nova's Pagar confirm releases the lock so Fornitalia can
 * confirm the Cobrar. PR 3.3.e.3.
 */
export type InterCoMovementItem = {
  id: number;
  /** sharepoint_id of this row. On the Pagar leg it doubles as the
   *  comprobante owner (Nova attaches the comprobante de pago to this
   *  row). On the Cobrar leg this is just the row's own SP id —
   *  callers wanting the file owner read `comprobante_owner_sp_id`. */
  sharepoint_id: number | null;
  sales_header_id: number | null;
  sales_header_sharepoint_id: number | null;
  nro_cotizacion: string | null;
  cliente: string | null;
  fecha: string;
  monto: number;
  currency: string;
  descripcion: string | null;
  cobro_locked: boolean;
  id_comprobante_pago_sp: number | null;
  /** Collection caja the customer paid into (Anticipo `medio_pago_origen`).
   *  The Pagar dialog locks its "caja origen" selector to this so the
   *  Pago Inter-Co debits the caja that actually holds the money. Null
   *  when unresolvable — dialog then allows a manual pick. */
  medio_pago_origen: string | null;
  /** Factura Interna PDF on the receipt referenced by
   *  `id_comprobante_pago_sp` (`es_factura_interna=true`). Surface via
   *  `FileLink entityType="FacturaVenta" entityId={String(id_comprobante_pago_sp)}`. */
  factura_file_name: string | null;
  factura_drive_item_id: string | null;
  /** Comprobante de pago Nova attaches when confirming the Pagar leg.
   *  Surfaced on both sides: the Pagar leg reads the row itself; the
   *  Cobrar leg reads the paired Pagar row (server resolves the
   *  pairing via `id_comprobante_pago_sp`). FileLink uses
   *  `entityType="ComprobanteInterCo"` +
   *  `entityId={String(comprobante_owner_sp_id)}`. */
  comprobante_file_name: string | null;
  comprobante_drive_item_id: string | null;
  /** sharepoint_id of the cash_movements row that owns the comprobante
   *  drive item (always the Pagar leg). Use as FileLink entityId. */
  comprobante_owner_sp_id: number | null;
  /** Receptor fiscal data — see `NovaFiscalData`. Surfaced on both
   *  Pagar and Cobrar legs so Nova has the receptor info when
   *  generating / cross-checking the CFDI factura. */
  fiscal_data: NovaFiscalData | null;
};

export async function listCuentasPorPagarNova(): Promise<InterCoMovementItem[]> {
  const res = await apiFetch<{ items: InterCoMovementItem[] }>(
    '/api/administracion/cuentas-por-pagar-nova',
  );
  return res.items;
}

export async function listCuentasPorCobrarFornitalia(): Promise<InterCoMovementItem[]> {
  const res = await apiFetch<{ items: InterCoMovementItem[] }>(
    '/api/administracion/cuentas-por-cobrar-fornitalia',
  );
  return res.items;
}

/**
 * Nova cajas the operator can pick to debit the Pagar Inter-Co from.
 * Mirrors the server-side `NOVA_PAGADOR_CAJAS` whitelist. BBVA
 * Italmexy is intentionally NOT here — that's Italmexy's bank, only
 * RECEIVES on the Cobrar Fornitalia confirm.
 */
export type NovaPagadorCaja = 'Nova Banorte' | 'Nova Mercado Pago' | 'Herconstru';

export async function confirmarPagarNova(
  id: number,
  cajaOrigen: NovaPagadorCaja,
): Promise<{
  ok: boolean;
  id: number;
  cajaOrigen: NovaPagadorCaja;
  egresoId: number | null;
  unlockedCobrarId: number | null;
}> {
  return apiFetch('/api/administracion/cuentas-por-pagar-nova/confirmar', {
    method: 'POST',
    body: { id, cajaOrigen },
  });
}

export type CajaDiferenciaResult =
  | { skipped: 'no_factura_interna' | 'no_costo_contable' | 'no_factura' | 'non_positive' | 'error'; reason?: string }
  | {
      skipped: false;
      diferencia: number;
      comision: number;
      ingresoId: number;
      egresoId: number | null;
    };

export async function confirmarCobrarFornitalia(
  id: number,
): Promise<{
  ok: boolean;
  id: number;
  /** Sharepoint/PK id of the BBVA Italmexy Ingreso that landed the
   *  inter-co receipt on Italmexy's books. Null only when the parent
   *  row had a non-positive monto or the insert errored (logged
   *  server-side, returned as null so the client doesn't fail). */
  ingresoItalmexyId: number | null;
  cajaDiferencia: CajaDiferenciaResult;
}> {
  return apiFetch('/api/administracion/cuentas-por-cobrar-fornitalia/confirmar', {
    method: 'POST',
    body: { id },
  });
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export type ConfirmarCobrosResult = {
  operacionId: string;
  confirmados: string[];
  statusRO: string | null;
  skipped: { id: string; reason: string }[];
};

export type ConfirmarCobrosResponse = {
  outcome: 'processed';
  results: ConfirmarCobrosResult[];
  notFound: string[];
  alreadyProcessed: { id: string; status: string }[];
};

export async function confirmarCobros(
  comprobanteIds: string[],
): Promise<ConfirmarCobrosResponse> {
  return apiFetch<ConfirmarCobrosResponse>('/api/administracion/cobros/confirmar', {
    method: 'POST',
    body: { comprobanteIds },
  });
}

/**
 * Stage of the two-stage MX factura flow.
 *  - `'anticipada'` (MX only): stamps `NroFacturaAnticipada_CP` without
 *    flipping `Facturado_CP`. The row stays in the Facturación queue
 *    until the Final is uploaded.
 *  - `'final'` (default, AR and MX): legacy behaviour — stamps
 *    `NroFactura_CP` and flips `Facturado_CP='SI'`.
 *
 * The server enforces the MX-only constraint on `'anticipada'`; AR
 * call sites should omit the field.
 */
export type FacturarStage = 'anticipada' | 'final';

export type FacturarResponse =
  | {
      outcome: 'facturado';
      stage?: FacturarStage;
      comprobanteId: string;
      nroFactura: string;
    }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'already_facturado'; message: string }
  | { outcome: 'invalid_stage'; message: string };

export async function facturarComprobante(
  comprobanteId: string,
  nroFactura: string,
  stage?: FacturarStage,
  montoOverride?: number,
): Promise<FacturarResponse> {
  const body: Record<string, unknown> = { comprobanteId, nroFactura };
  if (stage) body.stage = stage;
  if (montoOverride != null) body.montoOverride = montoOverride;
  return apiFetch<FacturarResponse>('/api/administracion/facturar', {
    method: 'POST',
    body,
  });
}

// --------------------------------------------------------------------------
// AR ARCA — electronic invoicing (CAE) via the AFIP SDK
// --------------------------------------------------------------------------

export type ArFacturaLinea = {
  codigo: string;
  articulo: string;
  observaciones: string;
  cantidad: number;
  /** Unit price, prorated by the cobro/venta ratio for partial facturas. */
  precioUnitario: number;
  descuentoPct: number;
  /** Post-discount, pre-IVA importe (prorated). */
  importe: number;
  /** IVA rate as a percentage (e.g. 10.5, 21). */
  ivaAlicuota: number;
  /** Importe con IVA (prorated). */
  importeConIva: number;
  /** Serialized items (hornos): N° de Serie + N° de Despacho (pedimento-
   *  equivalent), loaded at the despacho stage. Empty until then. Print-only. */
  nroSerie?: string;
  nroDespacho?: string;
};

/** The factura a Nota de Crédito references (AFIP CbtesAsoc). */
export type ComprobanteAsociado = {
  tipo: number;
  puntoVenta: number;
  numero: number;
  /** Pre-formatted label, e.g. "A-00005-00000123". */
  display: string;
};

/** Factura E / NC E (exportación, WSFEX): lo que se imprime además del comprobante común. */
export type ExportacionImpresa = {
  monedaIso: 'USD';
  /** Cotización declarada a ARCA (Moneda_ctz). */
  cotizacion: number;
  incoterm: string;
  /** Nº de permiso de embarque informado; null = sin permiso. */
  permiso: string | null;
  paisDestino: string;
  /** CUIT-país con el que ARCA identifica al receptor. */
  cuitPais: string;
  /** Número fiscal del cliente en su país (RUT, RFC…). */
  idImpositivo: string;
  /** Equivalente en pesos a la cotización declarada (informativo). */
  importePesos: number;
};

export type ArFacturaData = {
  /** Presente sólo en Factura E / NC E: importes en USD, sin IVA. */
  exportacion?: ExportacionImpresa;
  tipoComprobante: string;
  nroComprobante: string;
  fecha: string;
  cae: string;
  vencimiento: string | null;
  qrUrl: string;
  /** Display label, e.g. "Pesos Argentinos". */
  moneda: string;
  formaPago: string | null;
  /** Comprobante due date (defaults to fecha); distinct from the CAE vto. */
  fechaVencimiento: string | null;
  /** Set for a Nota de Crédito — the factura it credits (CbtesAsoc). */
  comprobanteAsociado?: ComprobanteAsociado | null;
  emisor: {
    razonSocial: string;
    cuit: string;
    puntoVenta: number;
    domicilio: string | null;
    ingresosBrutos: string | null;
    inicioActividades: string | null;
    condicionIva: string | null;
  };
  receptor: {
    nombre: string;
    documento: string;
    condicionIva: string | null;
    domicilio: string | null;
    provincia: string | null;
    ciudad: string | null;
  };
  lineas: ArFacturaLinea[];
  importes: {
    neto: number;
    iva21: number;
    iva10: number;
    exento: number;
    impuestos: number;
    total: number;
    /** Per-rate IVA totals for the breakdown row (ARCA models 10.5 & 21). */
    ivaBuckets: { '2.5': number; '5': number; '10.5': number; '21': number; '27': number };
  };
};

export type SolicitarCaeResponse =
  | {
      outcome: 'facturado';
      cae: string;
      vencimiento: string | null;
      nroComprobante: string;
      qrUrl: string;
      observacion?: string;
      factura: ArFacturaData;
      /** NC only: the NC receipt's list-item id, so the FE can attach the generated PDF to it. */
      comprobanteId?: string;
      /** NC only: a full NC reopened the original cobro (pendiente de facturar). */
      facturaReabierta?: boolean;
    }
  | {
      outcome: 'missing_fiscal_data';
      missing: Array<{ tag: string; message: string }>;
      /** Factura E: cotización de ARCA, para ofrecerla aunque el comprobante todavía no sea emitible. */
      cotizacionArca?: number | null;
    }
  | { outcome: 'already_facturado'; message: string }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'wrong_currency'; message: string }
  | {
      /** El cliente es del exterior: sólo admite Factura E (tipo 19). El diálogo cambia solo. */
      outcome: 'requiere_factura_e';
      message: string;
    }
  | { outcome: 'afip_rejected'; message: string }
  | { outcome: 'in_progress'; message: string }
  | {
      /** AFIP rejected a regular factura — must be emitted as MiPyME FCE.
       *  Operator should re-pick the tipo as "Factura de Crédito MiPyMEs". */
      outcome: 'fce_required';
      message: string;
    }
  | {
      /** FCE tipo chosen but the Punto de Venta has no CBU / transmisión. */
      outcome: 'fce_required_no_config';
      message: string;
    }
  | {
      /** NC-only: the credit amount exceeds the factura's remaining saldo. */
      outcome: 'nc_saldo_excedido';
      message: string;
      disponible: number;
      facturaTotal: number;
      /** 'USD' para una Factura E (saldo en dólares); ausente = pesos. */
      monedaIso?: 'USD';
    }
  | {
      outcome: 'dry_run';
      production: boolean;
      /** 'USD' cuando es una Factura E / NC E; ausente = pesos. */
      monedaIso?: 'USD';
      input: Record<string, unknown>;
      /** The exact line items that will print — editable (text only) before emit. */
      lineas: ArFacturaLinea[];
      /** Date ARCA will stamp (YYYY-MM-DD); número + CAE are assigned on emit. */
      fecha: string;
      resumen: CaeResumen;
      /** Vendor's tag (RegistrarPago "Pago Final") — the authoritative intent. */
      esPagoFinal?: boolean;
      /** Factura path only (undefined for NC dry-runs): true when this factura
       *  prints as "Anticipo a cuenta" (cobro doesn't cover the venta total). */
      esAnticipo?: boolean;
      /** True when this is the LAST pending factura of the venta yet the cobro is
       *  tagged as anticipo → suggest marking it Pago Final. */
      sugerirPagoFinal?: boolean;
      /** Venta grand total (ARS). */
      ventaTotal?: number;
      /** Σ of the venta's confirmed cobros (ARS) — to flag a fully-paid venta
       *  whose factura still comes out as an anticipo. */
      totalConfirmado?: number;
      /** True when the venta has hornos still missing N° de Serie / Despacho —
       *  the factura will print those blank; regenerate it after despacho. */
      faltanSerieDespacho?: boolean;
    };

/** Human-readable preview of exactly what a CAE request will send to ARCA —
 *  returned by the `dryRun` call and shown in the pre-emit confirmation. */
export type CaeResumen = {
  emisor: {
    razonSocial: string | null;
    cuit: string;
    puntoVenta: number;
    domicilio: string | null;
    ingresosBrutos: string | null;
    inicioActividades: string | null;
    condicionIva: string | null;
  };
  receptor: {
    nombre: string;
    documento: string;
    condicionIva: string | null;
    domicilio: string | null;
  };
  tipoComprobante: string;
  /** Set for a Nota de Crédito — the factura it credits (CbtesAsoc). */
  comprobanteAsociado?: ComprobanteAsociado | null;
  moneda: string;
  medioPago: string | null;
  /** True when the cobro is in a USD medio (monto is dollars). */
  esDolar: boolean;
  /** Raw cobro amount in the medio's currency (dollars if esDolar). */
  montoOriginal: number;
  /** Tipo de cambio applied when esDolar; null otherwise. */
  fxRate: number | null;
  /** Peso value actually invoiced (montoOriginal × fxRate for USD medios). */
  cobroPesos: number;
  importes: {
    neto: number;
    iva21: number;
    iva105: number;
    exento: number;
    total: number;
  };
  /** Sólo Factura E / NC E: importes en USD (sin IVA) y datos de exportación. */
  exportacion?: {
    monedaIso: 'USD';
    /** Cotización que se va a declarar (la de ARCA o la pisada por el operador). */
    cotizacion: number;
    /** La de ARCA (FEXGetPARAM_Ctz), si se consultó en esta llamada. */
    cotizacionArca: number | null;
    incoterm: string;
    permiso: string | null;
    paisDestino: string;
    cuitPais: string;
    idImpositivo: string;
    importePesos: number;
    esParcial?: boolean;
  };
};

/**
 * POST /api/administracion/solicitar-cae — AR-only.
 *
 * Requests a CAE from ARCA (via the AFIP SDK) for a single cobro, using the
 * operator-chosen `tipoComprobanteId`, then persists CAE + vencimiento + nº
 * + QR on the receipt. `dryRun` echoes the payload without calling AFIP.
 */
export async function solicitarCaeArca(args: {
  comprobanteId: string;
  tipoComprobanteId: number;
  conceptoId?: number;
  puntoVentaId?: number;
  dryRun?: boolean;
  /** FCE MiPyME only — payment-due date (FchVtoPago), YYYY-MM-DD. Omit → the
   *  server defaults to the comprobante date. Ignored for non-FCE tipos. */
  vencimientoPago?: string;
  /** Override the vendor's "Pago Final" tag from the Facturar dialog. Drives the
   *  printed líneas and is persisted onto the cobro on emit. Omit → vendor tag. */
  pagoFinal?: boolean;
  /** Per-line description overrides, index-aligned to the dry-run `lineas`
   *  (text only — the server keeps authoritative amounts/IVA). */
  lineasTexto?: string[];
  /** Factura E únicamente: cotización del dólar pisada por el operador (omitida → la de ARCA). */
  cotizacion?: number;
  /** Factura E únicamente: Incoterm (EXW, FOB…). Obligatorio. */
  incoterm?: string;
  /** Factura E únicamente: Nº de permiso de embarque (opcional). */
  permisoEmbarque?: string;
}): Promise<SolicitarCaeResponse> {
  const body: Record<string, unknown> = {
    comprobanteId: args.comprobanteId,
    tipoComprobanteId: args.tipoComprobanteId,
  };
  if (args.cotizacion != null) body.cotizacion = args.cotizacion;
  if (args.incoterm) body.incoterm = args.incoterm;
  if (args.permisoEmbarque) body.permisoEmbarque = args.permisoEmbarque;
  if (args.conceptoId != null) body.conceptoId = args.conceptoId;
  if (args.puntoVentaId != null) body.puntoVentaId = args.puntoVentaId;
  if (args.dryRun) body.dryRun = true;
  if (args.vencimientoPago) body.vencimientoPago = args.vencimientoPago;
  if (args.pagoFinal != null) body.pagoFinal = args.pagoFinal;
  if (args.lineasTexto?.length) body.lineasTexto = args.lineasTexto;
  return apiFetch<SolicitarCaeResponse>('/api/administracion/solicitar-cae', {
    method: 'POST',
    body,
  });
}

export type RegenerarFacturaResponse =
  | { outcome: 'ok'; factura: ArFacturaData; vencimientoImpreso?: string }
  | { outcome: 'not_arca'; message: string }
  | { outcome: 'missing_fiscal_data'; missing: Array<{ tag: string; message: string }> };

/**
 * GET /api/administracion/ar-factura-data/:comprobanteId — AR-only.
 *
 * Rebuilds the `ArFacturaData` for an already-issued factura (same CAE / número
 * / QR, líneas now carrying the N° de Serie / Despacho loaded at despacho), so
 * the FE can re-render the PDF and re-upload it. Print-only — no fiscal change.
 * `vencimiento` (yyyy-mm-dd) sets the printed "Fecha Vto." (persisted
 * server-side, not sent to ARCA; rejected for FCE tipos).
 */
export async function regenerarFacturaData(
  comprobanteId: string,
  opts?: { vencimiento?: string },
): Promise<RegenerarFacturaResponse> {
  const qs = opts?.vencimiento ? `?vencimiento=${encodeURIComponent(opts.vencimiento)}` : '';
  return apiFetch<RegenerarFacturaResponse>(
    `/api/administracion/ar-factura-data/${encodeURIComponent(comprobanteId)}${qs}`,
  );
}

/**
 * POST /api/administracion/solicitar-nota-credito — AR-only.
 *
 * Emits an electronic Nota de Crédito (fiscal-only) that credits an issued
 * factura, for its total or a custom partial `montoNC` (pesos). Returns the
 * same shape as `solicitarCaeArca` (dry_run preview / facturado), plus the
 * `nc_saldo_excedido` outcome when the credit exceeds the factura's saldo.
 */
export async function solicitarNotaCredito(args: {
  /** id (or sharepoint_id) of the facturado cobro being credited. */
  facturaReceiptId: string;
  /** Peso credit amount; omit for a full credit. */
  montoNC?: number;
  /** Client-minted idempotency key (one per dialog open). */
  idempotencyKey: string;
  puntoVentaId?: number;
  dryRun?: boolean;
  lineasTexto?: string[];
}): Promise<SolicitarCaeResponse> {
  const body: Record<string, unknown> = {
    facturaReceiptId: args.facturaReceiptId,
    idempotencyKey: args.idempotencyKey,
  };
  if (args.montoNC != null) body.montoNC = args.montoNC;
  if (args.puntoVentaId != null) body.puntoVentaId = args.puntoVentaId;
  if (args.dryRun) body.dryRun = true;
  if (args.lineasTexto?.length) body.lineasTexto = args.lineasTexto;
  return apiFetch<SolicitarCaeResponse>('/api/administracion/solicitar-nota-credito', {
    method: 'POST',
    body,
  });
}

// --------------------------------------------------------------------------
// Stage 3 — Retention preview + Pagar Factura
// --------------------------------------------------------------------------

export type RetencionPreviewRequest = {
  /** Pagar Factura path — the compra is persisted. */
  compraId?: string;
  /** Nueva Compra path — the compra isn't saved yet, send values inline. */
  draft?: {
    proveedorNombre: string;
    totalSinIVA: number;
    moneda: 'Pesos Argentinos' | 'Dolares';
  };
  conceptoConcat: string;
  tipoCambio?: number;
  /** `yyyy-mm-dd` — el mes del pago es el que acumula (RG 830). Sin ella, hoy. */
  fechaPago?: string;
};

/**
 * Path tag emitted per preview by the backend retention engine.
 *
 *  - `flat` / `scale_general` / `scale_119` — a regular calc fired,
 *    `retenido > 0`.
 *  - `below_threshold` — the base sits at/below the regime's
 *    per-concepto `MontosNoSujetosRetencion` (or, on scales, the
 *    bracket's `MontoExc`). `retenido === 0`.
 *  - `below_limite_retenciones` — calculated retention is smaller than
 *    the system-wide `Limite Retenciones` parameter; PowerApps would
 *    drop it with "no corresponde efectuar la retención". `retenido === 0`.
 *  - `unmatched` — no scale row covers the base; admin must enter a
 *    manual override. `retenido === 0`.
 *
 * The three zero-result tags carry a human-readable `reason` the UI
 * surfaces as a "No corresponde retención" banner.
 */
export type RetencionPreviewPath =
  | 'flat'
  | 'scale_general'
  | 'scale_119'
  | 'below_threshold'
  | 'below_limite_retenciones'
  | 'unmatched';

export const RETENCION_NO_CORRESPONDE_PATHS: ReadonlySet<RetencionPreviewPath> = new Set([
  'below_threshold',
  'below_limite_retenciones',
]);

export type RetencionPreviewResponse =
  | {
      outcome: 'computed';
      path: RetencionPreviewPath;
      base: number;
      retenido: number;
      compraMoneda: 'Pesos' | 'Dolares' | string;
      tipoCambio: number | null;
      reason?: string;
      bracket?: {
        desde: number;
        hasta: number;
        monto: number;
        porcentaje: number;
        montoExc: number;
      };
    }
  | { outcome: 'concepto_not_found'; message: string };

export async function previewRetencion(
  payload: RetencionPreviewRequest,
): Promise<RetencionPreviewResponse> {
  return apiFetch<RetencionPreviewResponse>('/api/administracion/retencion/preview', {
    method: 'POST',
    body: payload,
  });
}

export type PagarInstrumentoPayload = {
  medioPagoId: string;
  medio: string;
  categoriaTipo: 'Banco' | 'Efectivo';
  banco?: string;
  importe: number;
  importePesos?: number;
  tipoCambio?: number;
  descripcion?: string;
};

export type PagarRetencionPayload = {
  /**
   * SP item id when this row is a pre-existing `IDPago_RP='Pendiente'`
   * retencion that the user is *confirming* in this pago (seeded from
   * Nueva Compra). The backend will patch the existing row instead of
   * inserting a duplicate. Absent for retenciones added in this session.
   */
  existingId?: string;
  conceptoConcat: string;
  conceptoCodigo: string;
  tipo: string;
  descripcion?: string;
  nroComprobante: string;
  /** Sin fecha: el server estampa `fechaPago` — la retención se practica al pagar. */
  importeISAR: number;
  importeRetenido: number;
};

export type PagarCompraPayload = {
  fechaPago: string;
  recibo?: string;
  observaciones?: string;
  tipoCambio?: number;
  instrumentos: PagarInstrumentoPayload[];
  retenciones: PagarRetencionPayload[];
};

export type PagarCompraResponse =
  | {
      outcome: 'paid';
      pagoId: string;
      statusCO: 'Pago Parcial' | 'Pagado';
      instrumentosCreados: number;
      /**
       * Created instrumento ids in request order. Pair element-wise
       * with the request's `instrumentos[]` to upload per-instrumento
       * comprobantes after the pago is committed.
       */
      instrumentoIds: string[];
      retencionesCreadas: number;
      movimientosCreados: number;
      /** Ley 25.413 total (ARS) — non-zero only when the pago includes Galicia transfers. */
      leyTotal: number;
      /** How many MovimientoCajas rows were posted under `CuentaContable_MC='Ley 25.413'`. */
      leyRows: number;
    }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'exceeds_balance'; message: string }
  /** El pago dejaría una caja en negativo (misma guarda que Tesorería). */
  | { outcome: 'insufficient_saldo'; message: string }
  | { outcome: 'concurrent_modification'; message: string };

export async function pagarCompra(
  compraId: string,
  payload: PagarCompraPayload,
): Promise<PagarCompraResponse> {
  return apiFetch<PagarCompraResponse>(`/api/administracion/pagar/${compraId}`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Auto-fire wrapper around `POST /api/administracion/pagar/:pagoId/email`.
 * The server is fail-silent (returns `sent: false` with a reason
 * instead of throwing), so this helper just forwards the result. Call
 * sites should `void`-call it from a `.catch(() => {})` so a network
 * error never surfaces in the UI — matches the user's "no retry, no
 * error toast" preference.
 */
export type EnviarPagoFacturaEmailResponse =
  | {
      sent: true;
      to: string[];
      bcc: string | null;
      withRetencion: boolean;
    }
  | {
      sent: false;
      reason:
        | 'pago_not_found'
        | 'pago_anulado'
        | 'compra_not_found'
        | 'no_factura_id'
        | 'no_proveedor_email'
        | 'no_comprobante'
        | 'retencion_pending'
        | 'pdf_fetch_failed'
        | 'send_failed';
      error?: string;
    };

export async function enviarPagoFacturaEmail(
  pagoId: string,
): Promise<EnviarPagoFacturaEmailResponse> {
  return apiFetch<EnviarPagoFacturaEmailResponse>(
    `/api/administracion/pagar/${encodeURIComponent(pagoId)}/email`,
    { method: 'POST' },
  );
}
